import BackendApi from '@/services/backend.service'
import { ValidationError, RequestError } from '@/services/errorHandler.service'
import CustomException from '@/services/customException'
import Vue from 'vue'

const state = {
  workgroupAgent: {
    average_time: 0,
    user_id: null,
    agent_id: null,
    workgroup_id: null,
    company_name: null,
    company_phone: null,
    company_provider: '',
    name: '',
    is_online: false,
    
    auto_ticket_derivation_enabled: false,
    auto_ticket_derivation_interval: null,
    auto_ticket_derivation_fail_after_attempts: null,
    ai_assistance: false, /* check  */
    allow_absent: false, /* check */
    allow_tags: false, /* check */
    allow_quick_replies: false, /* check */
    allow_contact_edit: false, /* check */
    show_pending_attention_notification: true, /* check */
    show_black_list_button: false,

    // Other properties from your example
    meta_phone_id: null,
    whatsapp_api_source_id: null
  },
  isFetchingTicketsDrawer: false,
  ticketsByStatus: getInitialTicketsByStage(),
  selectedTicketPhone: null,
  selectedOmniContactId: null,
  selectedSenderId: null,
  stageExpandedId: null,
  singleTicketLoaders: {
    'pending': false,
    'in_attention': false,
    'success': false,
    'failed': false
  }
}

const getters = {
  fetched_tickets_count: (state) => (stage_name) => {
    const stage = state.ticketsByStatus.find((stage) => stage.name === stage_name)

    return stage.pinned.length + stage.unpinned.length
  },
  selected_ticket_phone: (state) => {
    return state.selectedTicketPhone
  },
  filtered_tickets: (state) => (filter) => {    
    const filteredTicketsByStage = state.ticketsByStatus.map((stage) => ({
      ...stage,
      pinned: stage.pinned.filter((ticket) => {
        if (ticket.client.tags?.length > 0) {
          return ticket.client.tags.some((tag) => tag.chatTagId === filter)
        }

        return false
      }),
      unpinned: stage.unpinned.filter((ticket) => {
        if (ticket.client.tags?.length > 0) {
          return ticket.client.tags.some((tag) => tag.chatTagId === filter)
        }

        return false
      }),
      count: stage.pinned.filter((ticket) => ticket.client.tags?.some((tag) => tag.chatTagId === filter)).length +
             stage.unpinned.filter((ticket) => ticket.client.tags?.some((tag) => tag.chatTagId === filter)).length
    }))
        
    return filteredTicketsByStage
  }
}

const mutations = {
  SET_WORKGROUP_AGENT(state, workgroupAgent) {
    state.workgroupAgent = workgroupAgent
  },
  SET_IS_FETCHING_TICKETS_DRAWER(state, isFetchingTicketsDrawer) {
    state.isFetchingTicketsDrawer = isFetchingTicketsDrawer
  },
  SET_TICKETS_BY_STATUS(state, ticketsByStatus) {
    state.ticketsByStatus = ticketsByStatus
  },
  SET_SELECTED_TICKET_PHONE(state, selectedTicketPhone) {
    state.selectedTicketPhone = selectedTicketPhone
  },
  SET_SELECTED_TICKET_IDENTIFIERS(state, { omni_contact_id, sender_id }) {
    state.selectedOmniContactId = omni_contact_id
    state.selectedSenderId = sender_id
  },
  SET_STAGE_EXPANDED_ID(state, stageExpandedId) {
    state.stageExpandedId = stageExpandedId
  },
  SET_STATUS_TICKETS_LOADER(state, { stage_name, is_loading }) {
    state.singleTicketLoaders[stage_name] = is_loading
  },
  UPDATE_AGENT_STATUS(state, newStatus) {
    if (newStatus !== 'online' && newStatus !== 'offline') {
      console.error('No se ha ingresado un status correcto', newStatus)
    } else {
      state.workgroupAgent.is_online = newStatus === 'online' ? 1 : 0
    }
  },
  ADD_TICKETS(state, { tickets, stage_name, add_type }) {
    const valid_names = ['pending', 'in_attention', 'success', 'failed']

    if (!tickets || !stage_name) throw new ValidationError('parametro no proporcionado', { tickets, stage_name, add_type })
    if (!valid_names.includes(stage_name)) throw new ValidationError('stage_name incorrecto', { stage_name, valid_names })

    const stage = state.ticketsByStatus.find((stage) => stage.name === stage_name)

    if (!stage) {
      console.error('stage no encontrado', { tickets, stage_name, add_type })

      return
    }

    if (add_type === 'unshift') {
      stage.pinned = [...tickets.pinned || [], ...stage.pinned]
      stage.unpinned = [...tickets.unpinned || [], ...stage.unpinned]
    } else {
      stage.pinned = [...stage.pinned, ...tickets.pinned || []]
      stage.unpinned = [...stage.unpinned, ...tickets.unpinned || []]
    }
  },
  ADD_TICKET(state, ticket) {
    const stage = state.ticketsByStatus.find((stage) => stage.status_id === ticket.status_id)
    if (ticket.is_pinned) {
      stage.pinned.unshift(ticket)      
    } else {
      stage.unpinned.unshift(ticket)
    }
  },
  DELETE_TICKET(state, ticket) {
    const stage = state.ticketsByStatus.find((stage) => stage.status_id === ticket.status_id)
    const targetArray = ticket.is_pinned ? stage.pinned : stage.unpinned
    const index = targetArray.findIndex((t) => parseInt(t.id) === parseInt(ticket.id))

    if (index !== -1) {
      targetArray.splice(index, 1)
    }
  },
  UPDATE_STAGE_COUNT(state, { stage_name, operation }) {
    const stage = state.ticketsByStatus.find((stage) => stage.name === stage_name)

    if (!stage) throw new ValidationError('cannot find stage', { stage_name, operation, stage })
    stage.count = stage.count + operation
  },
  SWITCH_TICKET_PIN_STATUS(state, { ticket }) {
    const stage = state.ticketsByStatus.find((stage) => stage.status_id === ticket.status_id)
    if (!stage) throw new ValidationError('No se ha encontrado el ticket', { stage, ticket })
    
    const newPinStatus = !ticket.is_pinned
    ticket.is_pinned = newPinStatus
    
    if (newPinStatus) {
      const index = stage.unpinned.findIndex((t) => parseInt(t.id) === parseInt(ticket.id))
      if (index !== -1) {
        const [movedTicket] = stage.unpinned.splice(index, 1)
        movedTicket.is_pinned = 1
        const messageDate = movedTicket.chat.last_message.created_at
        let insertIndex = stage.pinned.findIndex((ticket) => 
          ticket.chat.last_message.created_at <= messageDate
        )
        if (insertIndex === -1) insertIndex = stage.pinned.length
        stage.pinned.splice(insertIndex, 0, movedTicket)
      }
    } else {
      const index = stage.pinned.findIndex((t) => parseInt(t.id) === parseInt(ticket.id))
      if (index !== -1) {
        const [movedTicket] = stage.pinned.splice(index, 1)
        movedTicket.is_pinned = 0
        const messageDate = movedTicket.chat.last_message.created_at
        let insertIndex = stage.unpinned.findIndex((ticket) => 
          ticket.chat.last_message.created_at <= messageDate
        )
        if (insertIndex === -1) insertIndex = stage.unpinned.length
        stage.unpinned.splice(insertIndex, 0, movedTicket)        
      }
    }
  },
  UPDATE_DRAWER_TICKET_TAGS(state, { ticket, backendTags }) {
    if (!ticket) throw new ValidationError('No se ha proporcionado el ticket', { ticket })
    if (!ticket.client?.phone) throw new ValidationError('El ticket no tiene client.phone', { ticket })
    
    const stage = state.ticketsByStatus.find((stage) => stage.status_id === ticket.status_id)
    if (!stage) throw new ValidationError('No se ha encontrado el ticket', { stage, ticket })
    //const ticketTags = tags.filter((tag) => newTagsIds.includes(tag.chatTagId))
    let foundTicket = stage.pinned.find((t) => parseInt(t.id) === parseInt(ticket.id))
    if (!foundTicket) {
      foundTicket = stage.unpinned.find((t) => parseInt(t.id) === parseInt(ticket.id))
    }
    
    if (!foundTicket) {
      return
    }
    
    if (!Array.isArray(backendTags)) {
      return
    }
    
    const normalizedTags = backendTags.map((backendTag) => {
      if (!backendTag || typeof backendTag !== 'object') {
        return null
      }
      
      return {
        chatTagPhoneId: backendTag.chatTagPhoneId ?? null,
        chatTagId: backendTag.chatTagId ?? null,
        name: backendTag.name ?? '',
        description: backendTag.description ?? '',
        hexValue: backendTag.hexValue ?? '#000000'
      }
    }).filter((tag) => tag != null && tag.chatTagId != null)
    
    Vue.set(foundTicket.client, 'tags', normalizedTags)
  },
  SET_TICKETS_COUNTS(state, payload) {
    payload.forEach((stage_counts) => {
      const stage_tickets = state.ticketsByStatus.find((ticket) => ticket.name === stage_counts.name)

      stage_tickets.count = stage_counts.count
    })
  },
  RESET_TICKETS(state) {
    state.ticketsByStatus = getInitialTicketsByStage()
    state.ticketsCounts = {}
    state.isFetchingTicketsDrawer = false
    state.singleTicketLoaders = {}
  }
}

const actions = {
  async initTicketsSession({ dispatch, rootState, state }, { vueContext }) {
    await dispatch('fetchTicketsDrawer', { vueContext })

    if (!vueContext.channel) {
      const pusher = rootState.sockets.pusher
      vueContext.channel = pusher.subscribe(
        'chatDrawer_' + state.workgroupAgent.workgroup_id
      )
    }

    dispatch('listenNewTicket', { channel: vueContext.channel, vueContext })
    dispatch('listenNewMessage', { channel: vueContext.channel, vueContext })
    dispatch('listenTicketStatusUpdates', { channel: vueContext.channel, vueContext })
  },
  async fetchWorkgroupAgent({ commit }, { vueContext }) {
    try {
      const { user_chat_rol, supervised_agent_id } = vueContext.$route.params
      const response = await BackendApi.post('/chat/workgroup_agent', {
        user_chat_rol,
        supervised_agent_id
      })

      if (!response?.data?.success || !response?.data) {
        throw new RequestError(response, { chatRol, supervised_agent_id })
      }
      const workgroupAgent = response.data.data
      if (!workgroupAgent.company_phone) {
        try {
          const whatsappAccountResponse = await BackendApi.get('/meta/whatsapp_account')
          if (whatsappAccountResponse?.data?.success && whatsappAccountResponse?.data?.data) {
            const accounts = Array.isArray(whatsappAccountResponse.data.data) 
              ? whatsappAccountResponse.data.data 
              : [whatsappAccountResponse.data.data]
            
            const accountWithPhone = accounts.find(account => account.phone)
            if (accountWithPhone?.phone) {
              workgroupAgent.company_phone = accountWithPhone.phone
            }
          }
        } catch (whatsappAccountError) {
          console.warn('No se pudo obtener company_phone desde endpoint de fallback', whatsappAccountError)
        }
      }
      commit('SET_WORKGROUP_AGENT', workgroupAgent)
      return {
        hasErrors: false,
        requireConf: !workgroupAgent
      }
    } catch (error) {
      CustomException.handleError({ error }, vueContext)

      return {
        hasErrors: true,
        requireConf: undefined
      }
    }
  },
  async getPagedTicketsByStatus({ commit, state, getters }, { stage_name }, vueContext) {
    const { agent_id, workgroup_id } = state.workgroupAgent

    try {
      commit('SET_STATUS_TICKETS_LOADER', { stage_name, is_loading: true })
      if (!['pending', 'in_attention', 'success', 'failed'].includes(stage_name)) throw new ValidationError('No se ha proporcionado un stage_name correcto')
      if (!stage_name || !agent_id || !workgroup_id) throw new ValidationError('Se requiere el status_id, agent_id y workgroup_id ')

      const limit = 20
      const page = Math.ceil(getters.fetched_tickets_count(stage_name) / limit) + 1

      const response = await BackendApi.get('/chat/workgroup_agent_tickets', {
        params: {
          status_ids: getStatusIdsByNames([stage_name]),
          agent_id,
          workgroup_id,
          page,
          limit
        }
      })

      const tickets_data = response?.data?.data

      if (!tickets_data) throw new RequestError('El Request no ha devuelto data')
      const stage_tickets = tickets_data.find((stage_tickets) => stage_tickets.name === stage_name).tickets

      if (stage_tickets) commit('ADD_TICKETS', { tickets: stage_tickets, stage_name, add_type: 'push' })
      commit('SET_STATUS_TICKETS_LOADER', { stage_name, is_loading: false })
    } catch (error) {
      CustomException.handleError({ error }, vueContext)
      commit('SET_STATUS_TICKETS_LOADER', { stage_name, is_loading: false })
    }
  },
  async fetchTicketsDrawer({ commit, state, dispatch }, { vueContext }) {
    commit('SET_IS_FETCHING_TICKETS_DRAWER', true)
    try {
      const { agent_id, workgroup_id } = state.workgroupAgent

      commit('SET_TICKETS_BY_STATUS', getInitialTicketsByStage())
      const [response_counts] = await Promise.all([
        BackendApi.post('/chat/drawer_tickets_counts', { agent_id, workgroup_id }),
        dispatch('getPagedTicketsByStatus', { stage_name: 'pending' }, vueContext),
        dispatch('getPagedTicketsByStatus', { stage_name: 'in_attention' }, vueContext)
      ])

      const counts = response_counts.data.data

      if (!counts) throw new Error('No se pudo obtener la data de los contadores')
      commit('SET_TICKETS_COUNTS', counts)
      commit('SET_IS_FETCHING_TICKETS_DRAWER', false)

      await Promise.all([
        dispatch('getPagedTicketsByStatus', { stage_name: 'success' }, vueContext),
        dispatch('getPagedTicketsByStatus', { stage_name: 'failed' }, vueContext)
      ])
    } catch (error) {
      commit('SET_IS_FETCHING_TICKETS_DRAWER', false)
      CustomException.handleError({ error }, vueContext)
    }
  },
  async searchDrawerTickets({ commit, state, dispatch }, { vueContext, input }) {
    commit('SET_IS_FETCHING_TICKETS_DRAWER', true)
    try {
      commit('SET_TICKETS_BY_STATUS', getInitialTicketsByStage())
  
      const { agent_id, workgroup_id } = state.workgroupAgent
      const status_ids = [1, 2, 3, 4]
      const response = await BackendApi.get('/chat/workgroup_agent_tickets', {
        params: { status_ids, agent_id, workgroup_id, user_search_input: input }
      })
      if (!response?.data?.success || !response?.data?.data) {
        throw new RequestError(response)
      }
      response.data.data.forEach((stage) => {
        const pinned = stage.tickets?.pinned || []
        const unpinned = stage.tickets?.unpinned || []
        const enrichTicket = (ticket) => {
          if (ticket.os_highlight_field === 'message') {
            ticket._highlight = {
              field: ticket.os_highlight_field,
              html: ticket.highlight_message_html,
              messageId: ticket.highlight_message_id,
              term: input
            }
          } else if (ticket.os_highlight_field === 'display_name') {
            ticket._highlight = {
              field: ticket.os_highlight_field,
            }
          }
          if (ticket.rejection_reason_id) {
            ticket.rejection = {
              id: ticket.rejection_reason_id,
              title: ticket.rejection_reason_title
            }
          }
          if (ticket.success_reason_id) {
            ticket.success = {
              id: ticket.success_reason_id,
              title: ticket.success_reason_title
            }
          }
          return ticket
        }
        stage.pinned = pinned.map(enrichTicket)
        stage.unpinned = unpinned.map(enrichTicket)
        delete stage.tickets
      })
      commit('SET_TICKETS_BY_STATUS', response.data.data)
      if (input && response.data.data.every(stage => stage.count === 0)) {
        await dispatch('app/showToast', 'No se encontraron tickets para tu búsqueda', { root: true })
      }
      commit('SET_IS_FETCHING_TICKETS_DRAWER', false)
    } catch (error) {
      commit('SET_IS_FETCHING_TICKETS_DRAWER', false)
      CustomException.handleError({ error }, vueContext)
    }
  },
  async openChatTicket({ commit, dispatch }, data) {
    const { omni_contact_id, sender_id, ticket_phone, vueContext } = data
    try {
      let ticket
      if (omni_contact_id && sender_id) {
        ticket = await dispatch('findTicketByOmniContactAndSender', { omni_contact_id, sender_id })
      } else if (ticket_phone) {
        ticket = await dispatch('findTicketByPhone', ticket_phone)
      }
      
      if (!ticket) {
        throw new ValidationError('No se pudo encontrar el ticket', { omni_contact_id, sender_id, ticket_phone })
      }
      
      commit('SET_SELECTED_TICKET_PHONE', ticket.client.phone)
      if (ticket.client.omni_contact_id && ticket.sender?.id) {
        commit('SET_SELECTED_TICKET_IDENTIFIERS', { 
          omni_contact_id: ticket.client.omni_contact_id, 
          sender_id: ticket.sender.id 
        })
      }
      ticket.chat.unread_message_count = 0
    } catch (error) {
      CustomException.handleError({ error }, vueContext)
    }
  },
  deleteTicket({ commit }, ticket) {
    commit('DELETE_TICKET', ticket)

    return ticket
  },
  findTicketByPhone({ state }, phone) {
    phone = parseInt(phone)
    for (const stage of state.ticketsByStatus) {
      const foundInPinned = stage.pinned.find((ticket) => parseInt(ticket.client.phone) === phone)

      if (foundInPinned) return foundInPinned

      const foundInUnpinned = stage.unpinned.find((ticket) => parseInt(ticket.client.phone) === phone)

      if (foundInUnpinned) return foundInUnpinned
    }
    return null
  },
  findTicketById({ state }, ticketId) {
    const id = parseInt(ticketId)
    for (const stage of state.ticketsByStatus) {
      const foundInPinned = stage.pinned.find((ticket) => parseInt(ticket.id) === id)
      if (foundInPinned) return foundInPinned
      const foundInUnpinned = stage.unpinned.find((ticket) => parseInt(ticket.id) === id)
      if (foundInUnpinned) return foundInUnpinned
    }
    return null
  },
  findTicketByOmniContactAndSender({ state }, { omni_contact_id, sender_id }) {
    const omniContactId = parseInt(omni_contact_id)
    const senderId = parseInt(sender_id)
    for (const stage of state.ticketsByStatus) {
      const foundInPinned = stage.pinned.find((ticket) => 
        parseInt(ticket.client.omni_contact_id) === omniContactId && 
        parseInt(ticket.sender.id) === senderId
      )
      if (foundInPinned) return foundInPinned
      const foundInUnpinned = stage.unpinned.find((ticket) => 
        parseInt(ticket.client.omni_contact_id) === omniContactId && 
        parseInt(ticket.sender.id) === senderId
      )
      if (foundInUnpinned) return foundInUnpinned
    }
    return null
  },
  moveUpTicket({ dispatch, commit }, ticket) {
    dispatch('deleteTicket', ticket)

    commit('ADD_TICKET', ticket)
  },
  listenNewMessage({ dispatch, state }, { channel }) {
    channel.unbind('message_' + state.workgroupAgent.agent_id)
    channel.bind('message_' + state.workgroupAgent.agent_id, (eventData) => {
      const messageData = eventData.message.message
      const chatTicketId = messageData.chat_ticket_id
      const phone = messageData.client?.phone
      const omniChatConversationId = messageData.omni_chat_conversation_id
      
      let findTicketPromise = Promise.resolve(null)
      
      if (chatTicketId) {
        findTicketPromise = dispatch('findTicketById', chatTicketId)
          .then((ticket) => {
            if (ticket) {
              if (phone && parseInt(ticket.client.phone) !== parseInt(phone)) {
                return null
              }
              return ticket
            }
            return null
          })
          .then((ticket) => {
            if (ticket) return ticket
            
            if (phone) {
              return dispatch('findTicketByPhone', phone)
                .then((foundTicket) => {
                  if (!foundTicket) return null
                  
                  if (chatTicketId && parseInt(foundTicket.id) !== parseInt(chatTicketId)) {
                    return null
                  }
                  
                  if (omniChatConversationId && foundTicket.chat?.conversation_id) {
                    if (parseInt(omniChatConversationId) !== parseInt(foundTicket.chat.conversation_id)) {
                      return null
                    }
                  }
                  
                  return foundTicket
                })
            }
            return null
          })
      } else if (phone) {
        findTicketPromise = dispatch('findTicketByPhone', phone)
          .then((ticket) => {
            if (!ticket) return null
            
            if (omniChatConversationId && ticket.chat?.conversation_id) {
              if (parseInt(omniChatConversationId) !== parseInt(ticket.chat.conversation_id)) {
                return null
              }
            }
            
            return ticket
          })
      }
      
      findTicketPromise
        .then((find_ticket) => {
          if (!find_ticket) {
            throw new ValidationError('No se pudo encontrar el ticket', { 
              chatTicketId, 
              phone,
              omniChatConversationId,
              messageData: JSON.stringify(messageData).substring(0, 200) 
            })
          }
          
          find_ticket.chat.last_message = messageData
          if (find_ticket.chat.last_message.type === 'inbound') {
            new Audio(require('@/assets/sound/message_sound.mp3')).play()
            find_ticket.chat.unread_message_count++
          }
          dispatch('moveUpTicket', find_ticket)
        })
        .catch((error) => {
          CustomException.handleError({ rootMethod: 'listenNewMessage (callback)', error }, null)
        })
    })
  },
  listenTicketStatusUpdates({ dispatch, state }, { channel, vueContext }) {
    channel.unbind('ticket_status_updated_' + state.workgroupAgent.agent_id)
    channel.bind('ticket_status_updated_' + state.workgroupAgent.agent_id, (eventData) => {
      const parsedPayload = typeof eventData === 'string' ? JSON.parse(eventData) : eventData
      dispatch('handleTicketStatusUpdate', { eventPayload: parsedPayload, vueContext })
    })
  },
  async handleTicketStatusUpdate({ commit, dispatch }, { eventPayload, vueContext }) {
    const statusIdToName = { 1: 'pending', 2: 'in_attention', 3: 'success', 4: 'failed' }
    const ticketId = parseInt(eventPayload.ticketId)
    const statusId = parseInt(eventPayload.statusId)
    if (!statusIdToName[statusId]) return
    const targetStageName = statusIdToName[statusId]
    const foundTicket = await dispatch('findTicketById', ticketId)
    if (!foundTicket) {
      await dispatch('getPagedTicketsByStatus', { stage_name: targetStageName }, vueContext)
      return
    }
    const previousStageName = statusIdToName[foundTicket.status_id]
    if (previousStageName !== targetStageName) {
      commit('DELETE_TICKET', foundTicket)
      commit('UPDATE_STAGE_COUNT', { stage_name: previousStageName, operation: -1 })
    }
    foundTicket.status_id = statusId
    foundTicket.is_pinned = 0
    if (eventPayload.rejectionReason) {
      foundTicket.rejection = {
        id: eventPayload.rejectionReason.id,
        title: eventPayload.rejectionReason.title
      }
    } else {
      foundTicket.rejection = null
    }
    if (previousStageName !== targetStageName) {
      commit('ADD_TICKET', foundTicket)
      commit('UPDATE_STAGE_COUNT', { stage_name: targetStageName, operation: +1 })
    }
    if (vueContext) {
      vueContext.$emit('show-new-conversation-notification', 'Un ticket cambió de estado automáticamente')
    }
  },
  async handleNewTicket({ commit, dispatch, state }, { newTicket, vueContext }) {
    const foundTicket = await dispatch('findTicketByPhone', newTicket.client.phone)
    const statusIdToName = { 1: 'pending', 2: 'in_attention', 3: 'success', 4: 'failed' }
    if (foundTicket && [1, 2].includes(parseInt(foundTicket.status_id)) && newTicket.status_id !== 5) {
      return
    }
    if (foundTicket && [3, 4].includes(parseInt(foundTicket.status_id))) {
      commit('DELETE_TICKET', foundTicket)
      commit('UPDATE_STAGE_COUNT', { stage_name: statusIdToName[foundTicket.status_id], operation: -1 })
    }
    if (newTicket.status_id === 5) {
      vueContext.$emit('show-new-conversation-notification', 'Un ticket fue derivado a otro agente')
    }
    newTicket.status_id = 1
    newTicket.is_pinned = 0
    newTicket.chat.unread_message_count = 1
    commit('ADD_TICKET', newTicket)
    commit('UPDATE_STAGE_COUNT', { stage_name: 'pending', operation: +1 })
    new Audio(require('@/assets/sound/notification-rattlesnake.mp3')).play()
    vueContext.$emit('show-new-conversation-notification', 'Tienes una nueva conversación en Pendiente')
  },
  listenNewTicket({ dispatch, state }, { channel, vueContext }) {
    channel.unbind('new_ticket_' + state.workgroupAgent.agent_id)
    channel.bind('new_ticket_' + state.workgroupAgent.agent_id, async (rawData) => {
      const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData
      dispatch('handleNewTicket', { newTicket: parsed.conversation, vueContext })
    })
  },
  async UPDATE_DRAWER_TICKET_ALIAS({ dispatch }, { newAlias, omniContactId, ticket }) {
    const foundTicket = await dispatch('findTicketByPhone', ticket.client.phone)
    if (foundTicket) {
      //Vue.set(foundTicket.client, 'alias', newAlias)
      if (newAlias !== null) {
        Vue.set(foundTicket.client, 'name', newAlias)
      }
      Vue.set(foundTicket.client, 'omni_contact_id', omniContactId)
    }
  }
}

function getInitialTicketsByStage() {
  return [
    { pinned: [], unpinned: [], count: 0, status_id: 1, name: 'pending' },
    { pinned: [], unpinned: [], count: 0, status_id: 2, name: 'in_attention' },
    { pinned: [], unpinned: [], count: 0, status_id: 3, name: 'success' },
    { pinned: [], unpinned: [], count: 0, status_id: 4, name: 'failed' }
  ]
}

function getStatusIdsByNames(names) {
  if (!Array.isArray(names)) throw new ValidationError('Se espera un array en la función', { names })
  const status_list = { 'pending': 1, 'in_attention': 2, 'success': 3, 'failed': 4 }
  const ids = names.map((name) => status_list[name] || null)
  return ids
}


export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
